"use client";

import { useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { searchUnicorn, getUnicornStatusCounts } from "@/services/api/UnicornTracker";

export default function UnicornListsMobile({
  filters,
  statusFilter,
  searchQuery,
  currentPage,
  onDataUpdate,
}) {
  const loadingRef = useRef(false);
  const fetchIdRef = useRef(0);
  const accumulatedUnicornsRef = useRef([]);

  const searchParams = useSearchParams();
  const selectedRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const originFilter = searchParams.get("origin_filter");
  const currentSort =
    searchParams.get("sort") || "last_known_post_money_valuation";
  const currentSortOrder = searchParams.get("sort_order") || "desc";

  const buildSearchFilters = useCallback(
    (page) => {
      const searchFilters = {
        is_unicorn: true,
        page: page,
        per_page: 25,
        sort: currentSort,
        sort_order: currentSortOrder,
      };

      if (filters?.sectors?.length > 0) searchFilters.sectors = filters.sectors.join(",");
      if (filters?.cities?.length > 0) searchFilters.cities = filters.cities.join(",");
      if (selectedRegionId) searchFilters.unicorn_regions = selectedRegionId;
      if (selectedCountryId) searchFilters.countries = selectedCountryId;
      if (sectors) searchFilters.sectors = sectors;
      if (stages) searchFilters.stages = stages;
      if (valuationCondition && valuationAmount) {
        searchFilters.valuation_condition = valuationCondition;
        searchFilters.valuation_amount = valuationAmount;
      }
      if (yearFoundedCondition && yearFoundedYear) {
        searchFilters.year_founded_condition = yearFoundedCondition;
        searchFilters.year_founded_year = yearFoundedYear;
      }
      if (timeToUnicornMin) searchFilters.min_time_to_unicorn = timeToUnicornMin;
      if (timeToUnicornMax) searchFilters.max_time_to_unicorn = timeToUnicornMax;
      if (originFilter) {
        searchFilters.origin_filter = originFilter;
      }
      // Enable dual-listing (HQ here OR founded here) whenever a country/region
      // is in context, so transplant-out companies appear in their origin view.
      if (selectedCountryId || selectedRegionId) {
        searchFilters.match_founded_country = 1;
      }
      if (searchQuery) searchFilters.keyword = searchQuery;

      if (statusFilter?.fallen) searchFilters.include_lost_unicorn = 1;
      if (statusFilter?.alumni) searchFilters.include_alumni = 1;
      if (!statusFilter?.current) searchFilters.exclude_current_unicorn = 1;

      return searchFilters;
    },
    [
      filters,
      selectedRegionId,
      selectedCountryId,
      sectors,
      stages,
      valuationCondition,
      valuationAmount,
      yearFoundedCondition,
      yearFoundedYear,
      timeToUnicornMin,
      timeToUnicornMax,
      originFilter,
      currentSort,
      currentSortOrder,
      statusFilter,
      searchQuery,
    ],
  );

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        selectedRegionId,
        selectedCountryId,
        filters,
        sectors,
        stages,
        valuationCondition,
        valuationAmount,
        yearFoundedCondition,
        yearFoundedYear,
        timeToUnicornMin,
        timeToUnicornMax,
        originFilter,
        currentSort,
        currentSortOrder,
        statusFilter,
        searchQuery,
      }),
    [
      selectedRegionId,
      selectedCountryId,
      filters,
      sectors,
      stages,
      valuationCondition,
      valuationAmount,
      yearFoundedCondition,
      yearFoundedYear,
      timeToUnicornMin,
      timeToUnicornMax,
      originFilter,
      currentSort,
      currentSortOrder,
      statusFilter,
      searchQuery,
    ],
  );

  const countsFetchKey = useMemo(
    () =>
      JSON.stringify({
        selectedRegionId,
        selectedCountryId,
        filters,
        sectors,
        stages,
        valuationCondition,
        valuationAmount,
        yearFoundedCondition,
        yearFoundedYear,
        timeToUnicornMin,
        timeToUnicornMax,
        originFilter,
      }),
    [
      selectedRegionId,
      selectedCountryId,
      filters,
      sectors,
      stages,
      valuationCondition,
      valuationAmount,
      yearFoundedCondition,
      yearFoundedYear,
      timeToUnicornMin,
      timeToUnicornMax,
      originFilter,
    ],
  );

  useEffect(() => {
    async function fetchCounts() {
      try {
        const base = {};
        if (filters?.sectors?.length > 0) base.sectors = filters.sectors.join(",");
        if (filters?.cities?.length > 0) base.cities = filters.cities.join(",");
        if (selectedRegionId) base.unicorn_regions = selectedRegionId;
        if (selectedCountryId) base.countries = selectedCountryId;
        if (sectors) base.sectors = sectors;
        if (stages) base.stages = stages;
        if (valuationCondition && valuationAmount) {
          base.valuation_condition = valuationCondition;
          base.valuation_amount = valuationAmount;
        }
        if (yearFoundedCondition && yearFoundedYear) {
          base.year_founded_condition = yearFoundedCondition;
          base.year_founded_year = yearFoundedYear;
        }
        if (timeToUnicornMin) base.min_time_to_unicorn = timeToUnicornMin;
        if (timeToUnicornMax) base.max_time_to_unicorn = timeToUnicornMax;
        if (originFilter) base.origin_filter = originFilter;

        const countsRes = await getUnicornStatusCounts({
          params: {
            ...base,
            include_lost_unicorn: 1,
            include_alumni: 1,
            ...((selectedCountryId || selectedRegionId) && {
              match_founded_country: 1,
            }),
          },
        });

        const statusCounts = {
          current: countsRes.data?.current || 0,
          fallen: countsRes.data?.fallen || 0,
          alumni: countsRes.data?.alumni || 0,
        };

        if (onDataUpdate) onDataUpdate({ statusCounts });
      } catch (error) {
        console.error("Error fetching status counts:", error);
      }
    }
    fetchCounts();
  }, [countsFetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevFilterKeyRef = useRef(filterKey);

  useEffect(() => {
    const filterChanged = prevFilterKeyRef.current !== filterKey;
    prevFilterKeyRef.current = filterKey;

    if (filterChanged) {
      fetchIdRef.current += 1;
      accumulatedUnicornsRef.current = [];
      loadingRef.current = false;
      if (onDataUpdate) {
        onDataUpdate({ loading: true, unicorns: [], loadingMore: false, hasMore: true });
      }
    }

    const currentFetchId = fetchIdRef.current;
    const pageToFetch = filterChanged ? 1 : currentPage;

    async function fetchUnicorns() {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;

        if (onDataUpdate) {
          onDataUpdate(pageToFetch === 1 ? { loading: true } : { loadingMore: true });
        }

        const searchFilters = buildSearchFilters(pageToFetch);
        const searchRes = await searchUnicorn({ params: searchFilters });

        if (currentFetchId !== fetchIdRef.current) return;

        const searchData = searchRes.data || {};
        const newUnicorns = searchData.data || [];
        const hasMorePages = searchData.current_page < searchData.last_page;

        if (pageToFetch === 1) {
          accumulatedUnicornsRef.current = newUnicorns;
        } else {
          const existingIds = new Set(accumulatedUnicornsRef.current.map((u) => u.id));
          const uniqueNew = newUnicorns.filter((u) => !existingIds.has(u.id));
          accumulatedUnicornsRef.current = [...accumulatedUnicornsRef.current, ...uniqueNew];
        }

        if (onDataUpdate) {
          onDataUpdate({
            unicorns: accumulatedUnicornsRef.current,
            loading: false,
            loadingMore: false,
            hasMore: hasMorePages,
          });
        }
      } catch (error) {
        if (currentFetchId === fetchIdRef.current) {
          console.error("Error fetching unicorns:", error);
          if (pageToFetch === 1) {
            accumulatedUnicornsRef.current = [];
          }
          if (onDataUpdate) {
            onDataUpdate({ unicorns: [], loading: false, loadingMore: false, hasMore: false });
          }
        }
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          loadingRef.current = false;
        }
      }
    }

    fetchUnicorns();
  }, [currentPage, filterKey, buildSearchFilters, onDataUpdate]);

  return null;
}
